import { computed, type ComputedRef, onMounted, ref, type Ref, watch } from 'vue'
import { createGlobalState, useSessionStorage } from '@vueuse/core'
import { useRoute } from 'vue-router'
import { useBreadcrumbsStore } from '@/vue/stores/breadcrumbsStore'
import { useUserStore } from '@/vue/stores/userStore'
import type { BreadcrumbAsset, BreadcrumbCompany, BreadcrumbPortfolio } from './breadcrumb.types'
import { useCompanyHierarchy } from './use-company-hierarchy'
import { useCompanyList } from './use-company-list'

export type BreadcrumbResult = {
    company: ComputedRef<BreadcrumbCompany | null>
    portfolio: ComputedRef<BreadcrumbPortfolio | null>
    asset: ComputedRef<BreadcrumbAsset | null>
    portfolioNotFound: ComputedRef<boolean>
    assetNotFound: ComputedRef<boolean>
    isLoading: ComputedRef<boolean>
    isLoadingCompanies: Ref<boolean>
    isLoadingCompanyData: Ref<boolean>
    hasInitialized: Ref<boolean>
    getPortfolioById: (id: number) => BreadcrumbPortfolio | null
    getAssetById: (id: number) => BreadcrumbAsset | null
    getCompanyById: (id: number) => BreadcrumbCompany | null
    error: Ref<string | null>
}

// --- Internal singleton (not exported) ---

const _useBreadcrumbState = createGlobalState(() => {
    const breadcrumbsStore = useBreadcrumbsStore()
    const route = useRoute()

    const userStore = useUserStore()

    const {
        getCompanyById,
        fetchCompanies,
        isLoading: isLoadingCompanies,
    } = useCompanyList()

    const {
        getAssetById,
        getPortfolioById,
        fetchCompanyHierarchy,
        resolvedCompanyId,
        isLoading: isLoadingCompanyData,
    } = useCompanyHierarchy()

    const isLoading = computed(() => isLoadingCompanies.value || isLoadingCompanyData.value)

    const error = ref<string | null>(null)

    // Persist the last resolved company ID in session storage so it survives page refresh
    const persistedCompanyId = useSessionStorage<number | null>('scaler:selected-company-id', null)

    // Get ID from route params based on route structure
    const assetId = computed(() => 'asset_id' in route.params ? (Number(route.params.asset_id) || null) : null)
    const portfolioId = computed(() => 'portfolio_id' in route.params ? (Number(route.params.portfolio_id) || null) : null)

    const companyId = computed(() => {
        // Route param takes highest priority
        if ('company_id' in route.params && Number(route.params.company_id)) {
            return Number(route.params.company_id)
        }

        // Resolved company from data layer (set after fetching by portfolio/asset)
        if (resolvedCompanyId.value) {
            return resolvedCompanyId.value
        }

        // Fallback to breadcrumbsStore
        if (breadcrumbsStore.companyId) {
            return breadcrumbsStore.companyId
        }

        // Check for persisted company ID from session storage (before falling back to user's default)
        if (persistedCompanyId.value) {
            return persistedCompanyId.value
        }

        // Fallback to user's default company
        if (userStore.companyId) {
            return userStore.companyId
        }

        return null
    })

    const hierarchy = computed(() => {
        // If we have an asset ID, build the complete hierarchy from it
        if (assetId.value) {
            const asset = getAssetById(assetId.value)
            if (asset) {
                return {
                    asset: asset,
                    portfolio: asset.portfolio,
                    company: asset.company,
                }
            }
        }

        // If we have a portfolio ID, build hierarchy from it
        if (portfolioId.value) {
            const portfolio = getPortfolioById(portfolioId.value)
            if (portfolio) {
                return {
                    asset: null,
                    portfolio: portfolio,
                    company: portfolio.company,
                }
            }
        }

        // If we have a company ID, build hierarchy from it
        if (companyId.value) {
            const company = getCompanyById(companyId.value)
            if (company) {
                return {
                    asset: null,
                    portfolio: null,
                    company: company,
                }
            }
        }

        // Fallback to empty hierarchy
        return {
            company: null,
            portfolio: null,
            asset: null,
        }
    })

    // Convenience refs for current entity objects
    const company = computed(() => hierarchy.value.company)
    const portfolio = computed(() => hierarchy.value.portfolio)
    const asset = computed(() => hierarchy.value.asset)

    const hasInitialized = ref(false)

    const portfolioNotFound = computed(
        () => hasInitialized.value && !!portfolioId.value && portfolio.value === null,
    )
    const assetNotFound = computed(
        () => hasInitialized.value && !!assetId.value && asset.value === null,
    )

    onMounted(async () => {
        // TODO: Remove this once the old breadcrumb navigation is removed
        // This is a hack to make the old and new breadcrumb navigation work.
        breadcrumbsStore.setCompanyId(companyId.value)
        // Always fetch companies first
        await fetchCompanies()
        // Fetch company hierarchy based on which ID we have

        if (assetId.value) {
            // We have an asset ID - fetch by asset
            await fetchCompanyHierarchy({ byAssetId: assetId.value })
        } else if (portfolioId.value) {
            // We have a portfolio ID - fetch by portfolio
            await fetchCompanyHierarchy({ byPortfolioId: portfolioId.value })
        } else if (companyId.value) {
            // We have a company ID - fetch by company
            await fetchCompanyHierarchy({ byCompanyId: companyId.value })
        } else if (userStore.companyId) {
            // We have a user company ID - fetch by user company id
            await fetchCompanyHierarchy({ byCompanyId: userStore.companyId })
        }

        hasInitialized.value = true

        // TODO: Remove this once the old breadcrumb navigation is removed
        // Re-set the company ID in the breadcrumbs store to the resolved company ID
        breadcrumbsStore.setCompanyId(companyId.value)
    })

    // Persist any company ID change to session storage so it survives page refresh
    watch(companyId, (id) => {
        if (id !== persistedCompanyId.value) {
            persistedCompanyId.value = id
        }
    })

    return {
        // Entity objects
        company,
        portfolio,
        asset,

        // Not-found states
        portfolioNotFound,
        assetNotFound,

        // Loading states
        isLoading,
        isLoadingCompanies,
        isLoadingCompanyData,
        hasInitialized,

        // Lookups
        getPortfolioById,
        getAssetById,
        getCompanyById,

        error,
    }
})

/**
 * Returns the current navigation context: company, portfolio, and asset.
 *
 * All values are nullable and should be accessed with optional chaining.
 *
 * ```ts
 * const { company, portfolio, asset } = useBreadcrumb_experimental()
 * // company.value  → BreadcrumbCompany | null
 * // portfolio.value → BreadcrumbPortfolio | null
 * // asset.value     → BreadcrumbAsset | null
 * ```
 *
 * Always use optional chaining when accessing entity properties:
 * ```ts
 * const portfolioId = portfolio.value?.id
 * const companyName = company.value?.name
 * ```
 *
 * For functions that require entities to be present, add null guards:
 * ```ts
 * async function fetchData() {
 *     if (!portfolio.value) return
 *     // Safe to use portfolio.value.id here
 * }
 * ```
 *
 * ### Data layer composables
 * For direct access to company list or portfolio/asset data, use:
 * - `useCompanyList()` — flat list of companies
 * - `useCompanyHierarchy()` — portfolios and assets for the current company
 */
export function useBreadcrumb_experimental(): BreadcrumbResult {
    return _useBreadcrumbState()
}
